/**
 * Was die Karte über den Vektorzustand einer Nachricht SAGT -- an EINER Stelle.
 *
 * Bis zum 24.08. stand dort eine rohe Boolean-Pille: `message.vectorized`
 * entschied zwischen „Vektorisiert" und „Nicht vektorisiert", zwei Zustände
 * für eine Auswahl mit vier. Und in einem Fall war die Anzeige schlicht
 * falsch.
 *
 * 🔴 DER FALL: der RAGWorker wählt über ZWEI Zweige
 * (app/services/rag_queue.py),
 *
 *     vectorized IS false
 *     OR (vectorized_at IS NOT NULL AND vectorized_at < max(mc.updated_at))
 *
 * Der zweite ist die Selbstheilung: eine Nachricht, deren Bewertung sich nach
 * dem Ingest geändert hat, wird erneut eingesammelt — sie ist `vectorized =
 * true` und steht trotzdem in der Warteschlange. Die Karte sagte dazu
 * „Vektorisiert". Gegen die echte Datenbank belegt in
 * verification/probe_rag_queue.py (P3).
 *
 * ⚠️ Und ein dritter Zustand war still: `vectorized = false` MIT gesetztem
 * `vectorized_at` heißt, dass im Qdrant noch der ALTE Punkt steht — der Ingest
 * ist ein Upsert ohne Löschpfad. „Nicht vektorisiert" ist dort die halbe
 * Wahrheit.
 *
 * Welcher Zweig gegriffen hat, weiß nur der Dienst; die Karte hat
 * `vectorized` und `vectorized_at`, aber nicht `max(updated_at)` der
 * Bewertungszeilen. Deshalb liefert die Auskunft drei Eimer, und dieses Modul
 * beschriftet sie.
 *
 * Konvention wie hinweise.js, laden.js, shortcuts.js: eine Aussage bekommt
 * einen NAMEN, der Wortlaut daneben ist frei. Der Test prüft Schlüssel und
 * Bindung -- nie die Prosa.
 */

/**
 * Die sechs Zustände, die die Karte unterscheidet.
 *
 * vektorisiert  fertig, und der Vektor ist aktuell
 * offen         noch nicht vektorisiert -- und der Worker sieht sie nicht
 *               (z. B. weil sie nicht freigegeben ist)
 * wartet        noch nie vektorisiert, und der Worker wird sie holen
 * veraltet      es steht ein Vektor da, aber er ist überholt
 * abgeschaltet  sie wartet auf etwas, das niemand abholt
 * unbekannt     die Auskunft ist gekappt; über DIESE Nachricht sagt sie nichts
 */
export const VEKTOR = {
  vektorisiert: "Vektorisiert",
  offen: "Nicht vektorisiert",
  wartet: "Wartet auf Vektorisierung",
  veraltet: "Vektor veraltet",
  abgeschaltet: "Vektorisierung abgeschaltet",
  // ⚠️ „Vektorstand" und nicht „Zustand": die Warteschlangenpille DANEBEN
  // sagt bei gekappter Liste bereits „Zustand unbekannt" (MessageCard.vue,
  // warteZustand). Beide stehen in derselben Meta-Zeile — sind beide Listen
  // gekappt, stünde dort zweimal derselbe Satz für zwei verschiedene Dinge.
  // Gefunden hat das nicht der Blick auf die Karte, sondern der Test, der
  // verbietet, dass ein Satz dieses Moduls in der Karte getippt steht.
  unbekannt: "Vektorstand unbekannt",
};

/** Die Farbe je Zustand. Eine Stelle, damit amber wirklich amber bleibt. */
export const VEKTOR_KLASSE = {
  vektorisiert: "bg-green-50 text-green-800 border-green-200",
  offen: "bg-gray-50 text-gray-700 border-gray-200",
  wartet: "bg-blue-50 text-blue-800 border-blue-200",
  veraltet: "bg-amber-50 text-amber-800 border-amber-200",
  abgeschaltet: "bg-amber-50 text-amber-800 border-amber-200",
  unbekannt: "bg-gray-50 text-gray-700 border-gray-200",
};

/**
 * Wie oft der Vektorlauf eingestellt ist -- als lesbarer Satzteil.
 *
 * ⚠️ Die Zahl kommt VOM DIENST. Nennt die Auskunft sie nicht, wird sie auch
 * nicht behauptet: eine getippte Vorgabe hier wäre die zweite Wahrheit neben
 * RAG_WORKER_INTERVAL_SECONDS, und sie stünde ausgerechnet dann da, wenn
 * niemand den echten Wert kennt. Dieselbe Regel wie beim Bewertungs-Zwilling.
 */
export function taktSatz(sekunden) {
  if (typeof sekunden !== "number" || sekunden <= 0) return "";
  if (sekunden % 86400 === 0) {
    const t = sekunden / 86400;
    return t === 1 ? "einmal täglich" : `alle ${t} Tage`;
  }
  if (sekunden % 3600 === 0) {
    const h = sekunden / 3600;
    return h === 1 ? "stündlich" : `alle ${h} Stunden`;
  }
  if (sekunden % 60 === 0) {
    const m = sekunden / 60;
    return m === 1 ? "jede Minute" : `alle ${m} Minuten`;
  }
  return `alle ${sekunden} Sekunden`;
}

/**
 * Der Hinweistext je Zustand.
 *
 * ⚠️ Er sagt ausdrücklich NICHT, wann der nächste Lauf fällt. `next_run` lebt
 * im Speicher des Worker-Threads und ist von außen nicht ablesbar — die
 * Auskunft führt das Feld deshalb gar nicht erst. Was hier steht, ist das
 * eingestellte INTERVALL, also eine Tatsache über die Konfiguration. Der Satz
 * des Bewertungs-Zwillings („arbeitet die Warteschlange der Reihe nach ab und
 * fragt alle 10 Sekunden nach") ist hier nicht abschreibbar: die Vorgabe steht
 * auf sieben Tagen.
 */
export function vektorHinweis(zustand, status) {
  const takt = taktSatz(status && status.intervall_sekunden);
  const nachsatz = takt
    ? ` Der Vektorlauf ist auf ${takt} gestellt; wann der nächste fällt, ist von außen nicht ablesbar.`
    : "";

  switch (zustand) {
    case "vektorisiert":
      return "Vektorisiert — steht dem RAG als Beispiel zur Verfügung.";
    case "offen":
      return "Noch nicht vektorisiert. Der Vektorlauf hat sie derzeit nicht auf der Liste — er nimmt nur, was der eingestellte Prüfumfang zulässt.";
    case "wartet":
      return "Noch nie vektorisiert; der Vektorlauf wird sie holen." + nachsatz;
    case "veraltet":
      return "Die Bewertung hat sich geändert, seit der Vektor geschrieben wurde. Bis zum nächsten Lauf steht im RAG noch der alte Stand." + nachsatz;
    case "abgeschaltet":
      return "Diese Nachricht wartet auf Vektorisierung, aber der Vektorlauf ist abgeschaltet (disable_rag_worker). Es holt sie niemand ab.";
    case "unbekannt":
      return "Es warten mehr Nachrichten, als die Auskunft aufzählt — sie ist bei 500 gekappt. Ob diese hier dazugehört, ist damit nicht zu sagen.";
    default:
      return "";
  }
}
